import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api';

// ==================== Initial State ====================
const initialState = {
  suicideIntentAssessments: [],
  currentSuicideIntentAssessment: null,
  isLoading: false,
  error: null,
};

// ==================== Async Thunks ====================

// Fetch all (optionally by patient)
export const fetchSuicideIntentAssessments = createAsyncThunk(
  'suicideIntent/fetchAll',
  async (patientId = null, { rejectWithValue }) => {
    try {
      const url = patientId
        ? `/suicide-intent?patient_id=${patientId}`
        : '/suicide-intent';
      const response = await api.get(url);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
          'Failed to fetch Suicide Intent assessments'
      );
    }
  }
);

// Fetch single record
export const fetchSuicideIntentAssessment = createAsyncThunk(
  'suicideIntent/fetchOne',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/suicide-intent/${id}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
          'Failed to fetch Suicide Intent assessment'
      );
    }
  }
);

// Create
export const createSuicideIntentAssessment = createAsyncThunk(
  'suicideIntent/create',
  async (assessmentData, { rejectWithValue }) => {
    try {
      const response = await api.post('/suicide-intent', assessmentData);
      return response.data.data;
    } catch (error) {
      if (error.response?.status === 422) {
        return rejectWithValue(error.response.data.errors);
      }
      return rejectWithValue(
        error.response?.data?.error ||
          'Failed to create Suicide Intent assessment'
      );
    }
  }
);

// Update
export const updateSuicideIntentAssessment = createAsyncThunk(
  'suicideIntent/update',
  async ({ case_id, assessmentData }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/suicide-intent/case/${case_id}`, assessmentData);
      return response.data.data ?? response.data;
    } catch (error) {
      if (error.response?.status === 422) {
        return rejectWithValue(error.response.data.errors);
      }
      return rejectWithValue(
        error.response?.data?.error ||
          'Failed to update Suicide Intent assessment'
      );
    }
  }
);

// Delete
export const deleteSuicideIntentAssessment = createAsyncThunk(
  'suicideIntent/delete',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/suicide-intent/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
          'Failed to delete Suicide Intent assessment'
      );
    }
  }
);

// ==================== Slice ====================
const suicideIntentSlice = createSlice({
  name: 'suicideIntent',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentSuicideIntentAssessment: (state, action) => {
      state.currentSuicideIntentAssessment = action.payload;
    },
    clearCurrentSuicideIntentAssessment: (state) => {
      state.currentSuicideIntentAssessment = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // FETCH ALL
      .addCase(fetchSuicideIntentAssessments.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSuicideIntentAssessments.fulfilled, (state, action) => {
        state.isLoading = false;
        state.suicideIntentAssessments = action.payload;
      })
      .addCase(fetchSuicideIntentAssessments.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // FETCH ONE
      .addCase(fetchSuicideIntentAssessment.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSuicideIntentAssessment.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentSuicideIntentAssessment = action.payload;
      })
      .addCase(fetchSuicideIntentAssessment.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // CREATE
      .addCase(createSuicideIntentAssessment.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createSuicideIntentAssessment.fulfilled, (state, action) => {
        state.isLoading = false;
        state.suicideIntentAssessments.unshift(action.payload);
      })
      .addCase(createSuicideIntentAssessment.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // UPDATE
      .addCase(updateSuicideIntentAssessment.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateSuicideIntentAssessment.fulfilled, (state, action) => {
        state.isLoading = false;

        const updated = action.payload.data ?? action.payload;
        const index = state.suicideIntentAssessments.findIndex(
          (d) => d.id === updated.id
        );

        if (index !== -1) {
          state.suicideIntentAssessments[index] = updated;
        }

        if (
          state.currentSuicideIntentAssessment?.id === updated.id
        ) {
          state.currentSuicideIntentAssessment = updated;
        }
      })
      .addCase(updateSuicideIntentAssessment.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // DELETE
      .addCase(deleteSuicideIntentAssessment.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteSuicideIntentAssessment.fulfilled, (state, action) => {
        state.isLoading = false;
        state.suicideIntentAssessments = state.suicideIntentAssessments.filter(
          (d) => d.id !== action.payload
        );

        if (
          state.currentSuicideIntentAssessment?.id === action.payload
        ) {
          state.currentSuicideIntentAssessment = null;
        }
      })
      .addCase(deleteSuicideIntentAssessment.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearError,
  setCurrentSuicideIntentAssessment,
  clearCurrentSuicideIntentAssessment,
} = suicideIntentSlice.actions;

export default suicideIntentSlice.reducer;
